import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";

import { ApiError, getAuthMethods, googleSignInUrl } from "@/api/client";
import { AuthFrame } from "@/auth/AuthFrame";
import { useAuth } from "@/auth/AuthProvider";
import { landing, safeNext } from "@/auth/next";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Spinner } from "@/ui/primitives";

/** Email and password, outside the shell, and Google for Darkstar staff when the
 * deployment has it (ADR-0016). Sends the user on to `next` once signed in. */
export function SignInScreen() {
  const { state, signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState(false);
  const googleRefused = params.get("error") === "google";

  useEffect(() => {
    let live = true;
    getAuthMethods()
      .then((m) => live && setGoogle(m.google_staff))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  if ((state.status === "signed-in" || state.status === "staff") && !busy) {
    return <Navigate to={landing(state.status === "staff" ? "staff" : "member", next)} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const who = await signIn(email.trim(), password);
      void navigate(landing(who.kind, next), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "The server could not be reached.");
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame
      footer={
        <Link to="/forgot-password" className="text-muted hover:text-ink">
          Forgotten your password?
        </Link>
      }
    >
      <h1 className="text-lg font-semibold text-ink">Sign in</h1>
      <p className="mt-1 text-sm text-muted">Use the email your organization invited.</p>
      <form className="mt-5 space-y-4" onSubmit={(event) => void submit(event)}>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {googleRefused && !error ? (
          <Notice tone="danger">
            That Google account can&apos;t sign in here. Darkstar staff need an account set up
            first.
          </Notice>
        ) : null}
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-ink">Email</span>
          <Input
            type="email"
            name="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-ink">Password</span>
          <Input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <Button type="submit" variant="primary" className="w-full" disabled={busy}>
          {busy ? <Spinner className="size-4" /> : null}
          Sign in
        </Button>
      </form>
      {google ? (
        <div className="mt-5 border-t border-line pt-4 text-center text-sm">
          <a className="text-muted hover:text-ink" href={googleSignInUrl(next)}>
            Darkstar staff: sign in with Google
          </a>
        </div>
      ) : null}
    </AuthFrame>
  );
}
